import { IAction } from 'src/typings';
import {
  IFiltersSubtitle,
  IGeneralUnitInfo,
  IMarketplace,
  IMarketplacePagination,
  IMarketplaceRequestFilter,
  IMarketplaceStaticLinkParams,
  IViewTile,
  IViewTileType,
  IWhereConditions,
  IWhereConditionsRange,
} from 'src/components/profileComponents/Marketplace/types';
import {
  IMarketplaceAllFiltersState,
  IMarketplaceBaseFilterResponse,
} from 'src/typings/marketplace';
import { replaceObjectProperty } from 'src/components/Marketplace/helpers/replaceObjectProperty';
import _ from 'lodash';
import { IMarketplaceComplex, IMarketplaceMapComplex, IMarketplacePlacement } from 'src/typings/marketplaceTile';
import { ITranslationData } from 'src/store/requests/translations/reducer';
import { IAppRequest } from 'src/typings/app';
import { toggleLinkParams } from 'src/components/Marketplace/helpers';
import { IMarketplaceThemeConfig } from 'src/components/Marketplace/types';
import { ActionTypes } from './actions';

export interface IMarketplaceReducer {
  currentMarketplace: IMarketplace | undefined;
  allMarketplaces: IMarketplace[];
  language: string | undefined;
  filtersInitial: IMarketplaceBaseFilterResponse[];
  filters: IMarketplaceBaseFilterResponse[];
  allFilters: IMarketplaceAllFiltersState;
  filterWhereConditions: IWhereConditions[];
  filtersSubtitle: IFiltersSubtitle;
  activeFilter: string | undefined;
  viewTile: IViewTile;
  generalUnitInfo: IGeneralUnitInfo;
  pagination: IMarketplacePagination | null;
  complexesTile: IMarketplaceComplex[];
  placementsTile: IMarketplacePlacement[];
  complexesMapList: IMarketplaceMapComplex[];
  linkParams: IMarketplaceRequestFilter[];
  staticLinkParams: IMarketplaceStaticLinkParams[];
  isUnitsGroupedByProject: boolean;
  isLoading: boolean;
  selectedPlacement: IMarketplacePlacement | null;
  translations: ITranslationData | null;
  complexDefaultConfig: IAppRequest | null;
  resultColumnsGrid: {
    [IViewTileType.Complexes]: number | null,
    [IViewTileType.Placements]: number | null,
    [IViewTileType.Map]: number | null,
  };
  themeConfig: IMarketplaceThemeConfig | null;
}

export const initialState: IMarketplaceReducer = {
  currentMarketplace: undefined,
  allMarketplaces: [],
  language: undefined,
  filtersInitial: [],
  filters: [],
  allFilters: {
    isOpen: false,
  },
  filterWhereConditions: [],
  filtersSubtitle: {},
  activeFilter: undefined,
  viewTile: {
    type: IViewTileType.Complexes,
    pageNumber: 1,
  },
  generalUnitInfo: {
    complexesTotalCount: 0,
    placementsTotalCount: 0,
  },
  pagination: null,
  complexesTile: [],
  placementsTile: [],
  complexesMapList: [],
  linkParams: [],
  staticLinkParams: [],
  isUnitsGroupedByProject: false,
  isLoading: false,
  selectedPlacement: null,
  translations: null,
  complexDefaultConfig: null,
  resultColumnsGrid: {
    [IViewTileType.Complexes]: 4,
    [IViewTileType.Placements]: 4,
    [IViewTileType.Map]: 4,
  },
  themeConfig: {
    fonts: {
      fontFamily: ['Inter'],
      fontWeight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
      xs: { fontFamily: 'Inter' },
      sm: { fontFamily: 'Inter' },
      md: { fontFamily: 'Inter' },
      lg: { fontFamily: 'Inter', fontWeight: '500' },
    },
  },
};

export const marketplaceReducer = (
  state = initialState,
  { type, payload }: IAction,
): IMarketplaceReducer => {
  switch (type) {
    case ActionTypes.SET_MARKETPLACE: {
      return {
        ...state,
        currentMarketplace: payload,
      };
    }
    case ActionTypes.SET_MARKETPLACE_LANGUAGE: {
      return {
        ...state,
        language: payload,
      };
    }
    case ActionTypes.SET_MARKETPLACE_FILTERS_INITIAL: {
      return {
        ...state,
        filtersInitial: payload,
      };
    }
    case ActionTypes.SET_MARKETPLACE_FILTERS: {
      return {
        ...state,
        filters: payload,
      };
    }
    case ActionTypes.SET_MARKETPLACE_RESET_ALL_FILTERS_WHERE_CONDITIONS: {
      return {
        ...state,
        filterWhereConditions: [],
        linkParams: [],
        staticLinkParams: [],
      };
    }

    case ActionTypes.SET_MARKETPLACE_RESET_FILTERS_WHERE_CONDITIONS: {
      return {
        ...state,
        filterWhereConditions: [...state.filterWhereConditions].filter((item) => !payload.includes(item.propertyName)),
        linkParams: [...state.linkParams].filter((item) => !payload.includes(item.propertyName)),
      };
    }

    case ActionTypes.SET_MARKETPLACE_FILTERS_WHERE_CONDITIONS_NOT_REQUEST: {
      return {
        ...state,
        filterWhereConditions: payload,
      };
    }
    case ActionTypes.SET_MARKETPLACE_FILTERS_WHERE_CONDITIONS: {
      const foundConditions = (payload as IMarketplaceRequestFilter[])
        .map((item) => _.omit<IWhereConditions>((item as IMarketplaceRequestFilter), 'oldSystemName'));
      const find = [...state.filterWhereConditions]
        .filter((item) => {
          const condition = foundConditions
            .find((p) => {
              if ('equals' in p) {
                return JSON.stringify(p) === JSON.stringify(item);
              }
              if (('min' && 'max') in p) {
                return p.propertyName === item.propertyName;
              }
              return false;
            });
          if (!foundConditions || !condition) return false;
          const isEqual = JSON.stringify(item) === JSON.stringify(condition);
          const isEqualPropertyName = JSON.stringify(item.propertyName) === JSON.stringify(condition.propertyName);
          const isRangePropertyName = JSON.stringify(item.propertyName) === JSON.stringify(condition.propertyName)
              && (item as IWhereConditionsRange).min !== undefined
              && (item as IWhereConditionsRange).max !== undefined;
          return isEqual
              || (isEqual && isEqualPropertyName)
              || (!isEqual && isRangePropertyName);
        });

      let data = [...state.filterWhereConditions];
      let linkParams = [...state.linkParams];
      const deleteItem = (item: IWhereConditions[]) => {
        const temp = [...state.filterWhereConditions];
        const tempLinkParams = [...state.linkParams];
        item.forEach((request) => {
          const index = temp.indexOf(request);
          if ((request as IWhereConditionsRange).min !== undefined && (request as IWhereConditionsRange).max !== undefined) {
            temp.splice(index, 1, payload[0]);
            tempLinkParams.splice(index, 1, payload[0]);
          } else {
            temp.splice(index, 1);
            tempLinkParams.splice(index, 1);
          }
        });
        return {
          whereConditions: temp,
          linkParams: tempLinkParams,
        };
      };
      if (find.length) {
        data = deleteItem(find).whereConditions;
        // eslint-disable-next-line prefer-destructuring
        linkParams = deleteItem(find).linkParams;
      } else {
        data = [...state.filterWhereConditions, ...payload];
        linkParams = [...state.linkParams, ...payload];
      }
      return {
        ...state,
        filterWhereConditions: replaceObjectProperty([...data], ['oldSystemName']),
        linkParams,
        viewTile: {
          ...state.viewTile,
          pageNumber: 1,
        },
      };
    }
    case ActionTypes.SET_MARKETPLACE_COMPLEXES_TILE: {
      return {
        ...state,
        complexesTile: payload,
      };
    }
    case ActionTypes.SET_MARKETPLACE_PLACEMENTS_TILE: {
      return {
        ...state,
        placementsTile: payload,
      };
    }
    case ActionTypes.SET_MARKETPLACE_COMPLEXES_MAP_LIST: {
      return {
        ...state,
        complexesMapList: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_ACTIVE_FILTER: {
      return {
        ...state,
        activeFilter: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_VIEW_TILE: {
      return {
        ...state,
        viewTile: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_GENERAL_UNIT_INFO: {
      return {
        ...state,
        generalUnitInfo: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_UNITS_GROUPING: {
      return {
        ...state,
        isUnitsGroupedByProject: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_IS_LOADING: {
      return {
        ...state,
        isLoading: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACES: {
      return {
        ...state,
        allMarketplaces: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_SELECTED_PLACEMENT: {
      return {
        ...state,
        selectedPlacement: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_TRANSLATIONS: {
      return {
        ...state,
        translations: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_COMPLEX_DEFAULT_CONFIG: {
      return {
        ...state,
        complexDefaultConfig: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_PAGINATION_DATA: {
      return {
        ...state,
        pagination: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_STATIC_LINK_PARAMS: {
      const staticLinkData = [...state.staticLinkParams];
      return {
        ...state,
        staticLinkParams: !state.staticLinkParams.length ? toggleLinkParams(staticLinkData, payload) : staticLinkData,
      };
    }

    case ActionTypes.SET_MARKETPLACE_SORTING_ORDER: {
      return {
        ...state,
        viewTile: {
          ...state.viewTile,
          sortOrders: payload,
        },
      };
    }

    case ActionTypes.SET_MARKETPLACE_COLUMNS_GRID: {
      return {
        ...state,
        resultColumnsGrid: {
          ...state.resultColumnsGrid,
          [payload.type]: payload.value,
        },
      };
    }

    case ActionTypes.SET_MARKETPLACE_THEME: {
      return {
        ...state,
        themeConfig: payload,
      };
    }

    case ActionTypes.SET_MARKETPLACE_FILTERS_SUB_TITLE: {
      return {
        ...state,
        filtersSubtitle: {
          ...state.filtersSubtitle,
          [payload.filterName]: payload.value,
        },
      };
    }

    case ActionTypes.SET_MARKETPLACE_ALL_FILTERS_STATE: {
      return {
        ...state,
        allFilters: payload,
      };
    }

    default:
      return state;
  }
};
